/**
 * This is the driver for order submission.  Each topic is its own component that allows them
 * to easily be changed and order swapped around.
 */
import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import withStyles from '@mui/styles/withStyles';
import { Grid, Paper, Typography } from '@mui/material';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';

import AbEnvironment from '../auth/AbEnvironment';
import AccountSuspendMsg from '../ui/AccountSuspendMsg';
import LoadingSpinner from '../ui/LoadingSpinner';
import AccountFetch from '../account/AccountFetch';
import ServerError from '../ui/ServerError';

import StepCustomer from './StepCustomer';
import StepProjectSelection from './StepProjectSelection';
import StepResellerAddress from './StepResellerAddress';
import StepSubscriptions from './StepSubscriptions';
import StepServices from './StepServices';
import StepBudgetCustomer from './StepBudgetCustomer';
import StepBudgetExpenseToMe from './StepBudgetExpenseToMe';
import StepCC from './StepCC';
import StepEmailContact from './StepEmailContact';
import StepEmailTemplate from './StepEmailTemplate';

import PageTitle from '../ui/PageTitle';

const abEnvironment = new AbEnvironment();

const SubscriptionOrderQuery = graphql`
query SubscriptionOrderQuery($myAccountId: UUID!, $accountId: UUID!) {
  myAccount : accountTByAccountId(accountId: $myAccountId) {
    accountId
    accountName
    projectTsByResellerAccountId {
      totalCount
    }
  }
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountNum
    accountName
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    useBudgets
    initialBalanceAmount
    autoTopupActive
    rAutoTopupActive
    autoTopupAmount
    rAutoTopupAmount
    autoTopupThreshhold
    rAutoTopupThreshold
    maxMonthlyBudget
    rMaxMonthlyBudget
    maxCharge
    rMaxCharge
    budgetApprovedCount
    preApproveSubs
    rPreApproveSubs
    projectTsByCustomerAccountId {
      totalCount
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        walletTByCustomerWalletId {
          walletId
          initialBalanceAmount
          autoTopupActive
          rAutoTopupActive
          autoTopupAmount
          rAutoTopupAmount
          autoTopupThreshold
          rAutoTopupThreshold
          maxMonthlyBudget
          rMaxMonthlyBudget
          maxCharge      
          rMaxCharge
        }
      }
    }
    accountTByParentId {
      payFromBalance
      autoTopupActive
      autoTopupAmount
      autoTopupThreshhold
      initialBalanceAmount 
      paymentSourceTsByAccountId(condition: {psStatus: 0}, orderBy: PS_ORDER_ASC) {
        totalCount
        nodes {
          psType
        }
      }
    }
    customerAppTsByCustomerAccountId {
      nodes {
        customerAppId
        appStatus
        paymentOrder
        passThroughFees
        bcId
        sharedEmail
        showSubscription
        rAppMarkupPercentage
        rPaymentOrder
        rPassThroughFees
      }
    }
    customerServiceTsByCustomerAccountId {
      nodes {
        customerServiceId
        serviceStatus
      }
    }
  }
}
`;

const styles = theme => ({
  
  stepperPaper: {
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    borderBottomLeftRadius: "10px",
    maxWidth: 1200,
    minWidth: 400,
    width: "100%"
  },
  title: {
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: theme.palette.secondary.main,  
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px"
  },
  titleLine: {
    color : "white", 
    fontSize: "21px",
    fontWeight: "500",
    paddingLeft: "16px",
    paddingTop: "24px",
    paddingBottom: "8px"
  },
  stepper: {
    borderBottomRightRadius: "10px",
    borderBottomLeftRadius: "10px",
  },
});


class SubscriptionOrder extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      forceFetch: 0,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,

      dataLoaded: false,
      resellerAddressPresent: null,
      resellerHasFundingSource: false,

      activeStep: 0,
      skipped: new Set(),

      // These attributes were intended to determine what to show in certain steps, but due to forcing refreshes at times,
      // the values are not correct, and should not be used.
      addedSubsCount: 0,          // Legacy usage, to be removed when legacy components are removed.
      addedServicesCount: 0,      // Legacy usage, to be removed when legacy components are removed.
      budgetChanged: false,       // Legacy usage, to be removed when legacy components are removed.

      reseller: {
        companyName:   '',
        contactLastName: '', 
        contactFirstName: '', 
        contactEmail: ''
      },
  
      customer: {
        accountId: '',
        companyName: '',
        contactLastName: '', 
        contactFirstName: '', 
        contactEmail: '',
        preApproveSubs: '',
        rPreApproveSubs: '',
      },
      // Lack of project is an empty project instead of a null, not sure why
      project: {

      },
      user: {
        
      }
    };
  }

  componentDidMount() {    
    const params = new URLSearchParams(this.props.location.search);
    let step = params.get('step');
    if (step) {
      let stepInt = parseInt(step, 10);
      this.setState({activeStep: stepInt});
    }

    if (this.props.auth.isAuthenticatedReseller()) {
      //see if reseller has address
      this.fetchAccount();

      var customer = null;

      //reseller already logged in
      var reseller = {
        companyName:   abEnvironment.getUserAttribute('a_accountName'),
        contactLastName: abEnvironment.getUserAttribute('a_lastName'),  
        contactFirstName: abEnvironment.getUserAttribute('a_firstName'),
        contactEmail: abEnvironment.getUserAttribute('a_email')
      };

      let fromRequestApproval = false
      let paramPropString = localStorage.getItem('soCustomerParms');
      if (paramPropString != null)
      {
        let paramProps = JSON.parse(paramPropString);
        if (paramProps.fromRequestApproval && paramProps.fromRequestApproval === true) {
          fromRequestApproval = true;
        }
        customer = {
          accountId: paramProps.accountId,
          companyName: paramProps.companyName,
          contactLastName: paramProps.contactLastName, 
          contactFirstName: paramProps.contactFirstName, 
          contactEmail: paramProps.contactEmail
        };

        localStorage.removeItem('soCustomerParms');
      }

      if (customer && step) {
        this.setState({reseller: reseller, customer: customer, activeStep: step});
      } else if (customer && !fromRequestApproval) {
        this.setState({reseller: reseller, customer: customer, activeStep: 1});
      } else if (customer && fromRequestApproval) {
        this.setState({reseller: reseller, customer: customer, activeStep: 2});
      } else {
        this.setState({reseller: reseller, activeStep: 0});
      }
    } else {
      this.setState({dataLoaded: true});
    }    
  }

  /* See if there is an address1.  NOTE: this has to be done this way versus query renderer as we need to allow update of address */
  fetchAccount = () => {
    let accountId = abEnvironment.getUserAttribute('a_id');
    AccountFetch.fetch(accountId).toPromise()
    .then(af => {
      if (af.accountTByAccountId) {
        let resellerHasFundingSource = false;
        if (af.accountTByAccountId.paymentSourceTsByAccountId.totalCount > 0) {
          for (let i = 0; i < af.accountTByAccountId.paymentSourceTsByAccountId.nodes.length; i++) {
            let fs = af.accountTByAccountId.paymentSourceTsByAccountId.nodes[i];
            if (fs.psType === 0 || fs.psType === 1) {
              resellerHasFundingSource = true;
              break;
            }
          }  
        }

        if (af.accountTByAccountId.address1 === null || af.accountTByAccountId.address1 === '') {
          this.setState({dataLoaded: true, resellerAddressPresent: false, resellerHasFundingSource: resellerHasFundingSource});
        } else {
          this.setState({dataLoaded: true, resellerAddressPresent: true, resellerHasFundingSource: resellerHasFundingSource});
        }
      }
    })
    .catch(err => {
      //nothing to do....
    })
  }

  updateForceFetch = () => {
    let temp = this.state.forceFetch;
    temp++;
    this.setState({forceFetch: temp});
  }

  getSteps = (expenseToCustSubCount, expenseToMeSubCount, signUpSubCount, resellerHasProjects, budgetChanged) => {
    let serviceBillingActive = false;
    let serviceBilling = abEnvironment.getFlagValue("serviceBilling");
    if (serviceBilling != null && serviceBilling.flagValue === 1) {
      serviceBillingActive = true;
    }

    var steps = [];
    steps.push({text: 'Select a customer', component: 'StepCustomer'});
    //this will come back once we allow multiple projects 
    //if (resellerHasProjects) {
    //  steps.push({text: "What project is this for?", component: 'StepProjectSelection'});
    //}
    if (!this.state.resellerAddressPresent) {
      steps.push({text: "Let your customer know it's you", component: 'StepResellerAddress'});
    }

    steps.push({text: 'Add subscriptions to the project', component: 'StepSubscriptions'});

    // TODO: if the reseller is requesting auth to add future subscriptions, then we should ask for budgets
    if (expenseToCustSubCount > 0) {
      steps.push({text: "Set up an expense wallet funded by your customer", component: 'StepBudgetCustomer'});
    }
    
    if (expenseToMeSubCount > 0) {
      steps.push({text: "Set up your expense wallet", component: 'StepBudgetExpenseToMe'});
    }

    if (signUpSubCount > 0) {
      steps.push({text: "Request credit card?", component: 'StepCC'});
    }

    if (serviceBillingActive === true) {
      steps.push({text: 'Add your services to the project', component: 'StepServices'});
    }
 
    if (this.state.customer.preApproveSubs === 1 && budgetChanged === false) {
      steps.push({text: 'Who will be notified?', component: 'StepEmailContact'});
      steps.push({text: 'Send for notification', component: 'StepEmailTemplate'});
    } else {
      steps.push({text: 'Who will approve this?', component: 'StepEmailContact'});
      steps.push({text: 'Send for approval', component: 'StepEmailTemplate'});
    }
   
    return steps;
  }
  
  getStepContent = (step, component, project, resellerHasProjects, budgetChanged) => {
    if (component === 'StepResellerAddress') {
      // Reseller set address step (edge FTU case)
      return (
        <StepResellerAddress auth={this.props.auth}
              assignedStep={step}
              activeStep={this.state.activeStep}
              handleBack={this.handleBack}
              handleNext={this.handleNext}/>
      );
    } else if (component === 'StepCustomer') {
      // Customer selection step
      return (
        <StepCustomer auth={this.props.auth}
                      history={this.props.history}
                      useProjects={resellerHasProjects}
                      customer={this.state.customer}
                      setParentStatesChanged={this.setStatesChanged}
                      assignedStep={step}
                      activeStep={this.state.activeStep}
                      handleBack={this.handleBack}
                      handleNext={this.handleNext}
        />
      );
    } else if (component === 'StepProjectSelection' && resellerHasProjects) {
      // Project selection step
      return (
        <StepProjectSelection auth={this.props.auth}
                              history={this.props.history}
                              customer={this.state.customer}
                              setProjectStateChanged={this.setProjectStateChanged}
                              project={project}
                              assignedStep={step}
                              activeStep={this.state.activeStep}
                              handleBack={this.handleBack}
                              handleNext={this.handleNext}
        />
      );
    } else if (component === 'StepSubscriptions') {
      return (
        <StepSubscriptions auth={this.props.auth}
                           customer ={this.state.customer}
                           project={project}
                           forceParentQuery={this.updateForceFetch}
                           assignedStep={step}
                           activeStep={this.state.activeStep}
                           handleBack={this.handleBack}
                           handleNext={this.handleNextOnSubscriptions}/>
      );
    } else if (component === 'StepServices') {
      return (
        <StepServices auth={this.props.auth}
                      customer ={this.state.customer}
                      project={project}
                      forceParentQuery={this.updateForceFetch}
                      assignedStep={step}
                      activeStep={this.state.activeStep}
                      handleBack={this.handleBack}
                      handleNext={this.handleNextOnServices}/>
      );
    } else if (component === 'StepBudgetCustomer') {
      return (
        <StepBudgetCustomer auth={this.props.auth}
                            history={this.props.history}
                            customer={this.state.customer}
                            project={project}
                            assignedStep={step}
                            activeStep={this.state.activeStep}
                            handleBack={this.handleBack}
                            handleNext={this.handleNextOnBudget}/>
      );
    } else if (component === 'StepBudgetExpenseToMe') {
      return (
        <StepBudgetExpenseToMe  auth={this.props.auth}
                                history={this.props.history}
                                customer={this.state.customer}
                                project={project}
                                resellerAddressPresent={this.state.resellerAddressPresent}
                                assignedStep={step}
                                activeStep={this.state.activeStep}
                                handleBack={this.handleBack}
                                handleNext={this.handleNext}/>
      );
    } else if (component === 'StepCC') {
      return (
        <StepCC auth={this.props.auth}
                                history={this.props.history}
                                customer={this.state.customer}
                                project={project}
                                assignedStep={step}
                                activeStep={this.state.activeStep}
                                handleBack={this.handleBack}
                                handleNext={this.handleNext}/>
      );
    } else if (component === 'StepEmailContact') {
      return (
          <StepEmailContact auth={this.props.auth}
                            customer={this.state.customer}
                            setUserStateChanged={this.setUserStateChanged}
                            user={this.state.user}
                            assignedStep={step}
                            activeStep={this.state.activeStep}
                            handleBack={this.handleBack}
                            handleNext={this.handleNext}/>
        );
    } else if (component === 'StepEmailTemplate') {
      return (
        <StepEmailTemplate auth={this.props.auth}
                           customer={this.state.customer}
                           history={this.props.history}
                           user={this.state.user}
                           project={project}
                           assignedStep={step}
                           activeStep={this.state.activeStep}
                           handleBack={this.handleBack}
                           handleNext={this.handleNext}/>
      );
    } else {
      return (
        <div>Unknown step</div>
      );
    }
  }
  
  handleNextOnSubscriptions = (addedSubsCount) => {
    let newStep = this.state.activeStep;
    newStep++;
    this.setState({activeStep: newStep, addedSubsCount: addedSubsCount});
  }

  handleNextOnServices = (addedServicesCount) => {
    let newStep = this.state.activeStep;
    newStep++;
    this.setState({activeStep: newStep, addedServicesCount: addedServicesCount});
  }
    
  handleNextOnBudget = (budgetChanged) => {
    let newStep = this.state.activeStep;
    newStep++;
    this.setState({activeStep: newStep, budgetChanged: budgetChanged});
  }

  handleNext = () => {
    let newStep = this.state.activeStep;
    newStep++;
    this.setState({activeStep: newStep});
  }

  handleBack = () => {
    let newStep = this.state.activeStep;
    newStep--;
    this.setState({activeStep: newStep});
  }

  setCustomerStateChanged = (akey, avalue) => {
    let customer = this.state.customer;
    customer[akey] = avalue;
    this.setState({customer: customer});
  }

  /**
   * This enable state setting at the parent level such that all state
   * values can be changed in on call.
   * 
   * @param {*} statePairs 
   */
  setStatesChanged = (customerStatePairs, project) => {
    let customer = null;
    if (customerStatePairs != null) {
      // for customer, we expect a an array of json objects:  [{key: <akey>, value: <avalue>}, {key: <akey>, value: <avalue>}, ...]
      customer = this.state.customer;
      for (let i = 0; i < customerStatePairs.length; i++) {
        let statePair = customerStatePairs[i];
        let key = statePair.key;
        let value = statePair.value;
        customer[key] = value;
      }
    }

    if (project == null) {
      if (customer != null) {
        this.setState({customer: customer});
      } 
    } else {
      if (customer == null) {
        this.setState({project: project});
      } else {
        this.setState({customer: customer, project: project});
      }
    }
  }

  setUserStateChanged = (avalue) => {
    this.setState({user: avalue});
  }
  
  setProjectStateChanged = (avalue) => {
    this.setState({project: avalue});
  }

  render() {
    const { isAuthenticated, isAuthenticatedReseller, isAccountSuspended, needAssent, logError } = this.props.auth;
    const { classes } = this.props;

    if (!isAuthenticated() || needAssent() || !isAuthenticatedReseller()) {
      return (
        <Redirect to='/home' />
      );
    } else {
      if (isAccountSuspended()) {
        let accountType = abEnvironment.getUserAttribute("a_aType");
        let accountStatus = abEnvironment.getUserAttribute("a_aStatus");

        return (
          <div className={classes.root}>
            
            <AccountSuspendMsg accountType={accountType} accountStatus={accountStatus}/>
          </div>
        );
      }

      if (!this.state.dataLoaded) {
        return <LoadingSpinner />
      }

      let acctId = this.state.customer.accountId;
      if (!acctId) {
        //did this so fetch did not error when customer was not selected yet.
       acctId = '00000000-0000-0000-0000-000000000000';
      }
      let myAccountId = abEnvironment.getUserAttribute("a_id");

      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={SubscriptionOrderQuery}
          variables={{myAccountId: myAccountId, accountId: acctId, forceFetch: this.state.forceFetch}}
          render={({error, props})=> {
            if (error) {
               logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props && this.state.dataLoaded) {
              // console.log(JSON.stringify(props));

              let theAccount = props.accountTByAccountId;

              let project = null;
              let resellerHasProjects = null;

              if (props.myAccount && props.myAccount.projectTsByResellerAccountId.totalCount > 0) { 
                resellerHasProjects = true;
              }

              if (theAccount && theAccount.projectTsByCustomerAccountId.nodes.length > 0) { 
                project = theAccount.projectTsByCustomerAccountId.nodes[0];
              }
              let expenseToCustSubCount = 0;
              let expenseToMeSubCount = 0;
              let signUpSubCount = 0;
  
              if (theAccount && theAccount.customerAppTsByCustomerAccountId.nodes.length > 0) {
                for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
                  let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
                  //exclude cancelled subs
                  if (sub.appStatus !== 2) {
                    if (sub.rPaymentOrder !== null) {
                      if (sub.rPaymentOrder === 0 || sub.rPaymentOrder === 1) {
                        expenseToCustSubCount++
                      } else if (sub.rPaymentOrder === 2) {
                        expenseToMeSubCount++;
                      } else if (sub.rPaymentOrder === 5) {
                        signUpSubCount++;
                      }
                    } else {
                      if (sub.paymentOrder === 0 || sub.paymentOrder === 1) {
                        expenseToCustSubCount++
                      } else if (sub.paymentOrder === 2) {
                        expenseToMeSubCount++;
                      } else if (sub.paymentOrder === 5) {
                        signUpSubCount++;
                      }
                    }
                  }
                }
              }

              // budget changes
              let budgetChanged = false;
              let budgetExists = false;
              if (theAccount) {
                if (theAccount.autoTopupAmount > 0) {
                  budgetExists = true;
                }

                if (budgetExists === false && (theAccount.rAutoTopupAmount > 0 || theAccount.initialBalanceAmount > 0 || theAccount.rMaxMonthlyBudget > 0)) {
                  budgetChanged = true;
                } else {
                  if (theAccount.rAutoTopupAmount    !== theAccount.autoTopupAmount     ||
                      theAccount.rAutoTopupThreshold !== theAccount.autoTopupThreshhold ||
                      theAccount.rMaxMonthlyBudget   !== theAccount.maxMonthlyBudget       ) {
                    budgetChanged = true;
                  }
                }
              }

              return (
                <React.Fragment>
                    <PageTitle title="Set up project" subtitle="Select subscriptions and services for your customer's project."/>
                    <Paper className={classes.stepperPaper}>
                      <Grid container>
                        <Grid item xs={12}>
                          <div className={classes.title} >
                            <Typography variant="h4" className={classes.titleLine} >
                              {"Subscription order"}
                            </Typography>
                          </div>
                        </Grid>
                        
                        <Grid item  xs={12}>
                          <Stepper activeStep={this.state.activeStep} orientation="vertical" className={classes.stepper} style={{margin: 16}}>
                            {this.getSteps(expenseToCustSubCount, expenseToMeSubCount, signUpSubCount, resellerHasProjects, budgetChanged).map((element, index) => (
                              <Step key={element.text} expanded={this.state.activeStep >= index}>
                                <StepLabel>{element.text}</StepLabel>
                                <StepContent>
                                    {this.getStepContent(index, element.component, project, resellerHasProjects, budgetChanged)}
                                </StepContent>
                              </Step>
                            ))}
                          </Stepper>
                        </Grid>
                      </Grid>
                    </Paper>
      
                </React.Fragment>
              );
            } else {
                return (<LoadingSpinner />);
            }
          }}
        />
      );

    }
  }
}

export default withStyles(styles)(SubscriptionOrder);